import {
  AccessRole,
  AccessRoleCatalog,
  BASE_ACCESS_ROLE,
} from '@trustero/trustero-api-web/lib/authz/authz_pb'
import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { UserGroupRecord } from '@trustero/trustero-api-web/lib/usergroup/usergroup_pb'
import { getEnumKeyName } from 'src/Utils/helpers/string.helpers'
import {
  indexCatalog,
  resolveCapabilityRule,
  resolveDomainRole,
  getPermissionOverrides,
} from 'src/pages/Users/userRoles/userRoles.helpers'
import type {
  ImpersonationInfo,
  PermissionInfo,
  Permissions,
} from 'src/context/authContext'

export const IMPERSONATE_ROLE_PARAM = 'impersonate_role'
export const IMPERSONATE_GROUPS_PARAM = 'impersonate_groups'
export const IMPERSONATE_ALLOWED_ROUTES_PARAM = 'impersonate_routes'
export const IMPERSONATION_SESSION_STORAGE_KEY =
  'trustero_impersonation_session'

const IMPERSONATION_PARAMS = [
  IMPERSONATE_ROLE_PARAM,
  IMPERSONATE_GROUPS_PARAM,
  IMPERSONATE_ALLOWED_ROUTES_PARAM,
]

// Preview mode lives in sessionStorage so it stays scoped to the tab it was
// opened in. Every access is guarded because sessionStorage throws in some
// privacy modes - losing it only costs the preview surviving a reload.
export const readImpersonationSession = (): ImpersonationInfo | undefined => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as ImpersonationInfo
    return parsed?.isImpersonating ? parsed : undefined
  } catch (e) {
    return undefined
  }
}

export const writeImpersonationSession = (info: ImpersonationInfo): void => {
  try {
    sessionStorage.setItem(
      IMPERSONATION_SESSION_STORAGE_KEY,
      JSON.stringify(info),
    )
  } catch (e) {
    // ignore
  }
}

export const clearImpersonationSession = (): void => {
  try {
    sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

export const baseAccessRoleToUserRole = (base: BASE_ACCESS_ROLE): USER_ROLE => {
  switch (base) {
    case BASE_ACCESS_ROLE.ADMIN:
      return USER_ROLE.ADMIN
    case BASE_ACCESS_ROLE.USER:
      return USER_ROLE.USER
    case BASE_ACCESS_ROLE.AUDITOR:
    case BASE_ACCESS_ROLE.AUDITOR_QC:
      return USER_ROLE.AUDITOR
    case BASE_ACCESS_ROLE.READ_ONLY:
    case BASE_ACCESS_ROLE.NO_ACCESS:
      return USER_ROLE.READONLY
    case BASE_ACCESS_ROLE.TP_PUBLISHER:
      return USER_ROLE.TRUSTPORTAL_PUBLISHER
    case BASE_ACCESS_ROLE.TP_APPROVER:
      return USER_ROLE.TRUSTPORTAL_APPROVER
    default:
      return USER_ROLE.READONLY
  }
}

export type EffectiveLabelRestrictions = {
  readOnlyLabelIds: string[]
  contentRestrictedLabelIds: string[]
  noAccessLabelIds: string[]
  fullAccessLabelIds: string[]
}

export const combineLabelRestrictions = (
  roleLabels?: {
    readOnlyLabelIds?: string[]
    contentRestrictedLabelIds?: string[]
    noAccessLabelIds?: string[]
  },
  groupLabels: Array<{
    labelIds?: string[]
    fullAccessLabelIds?: string[]
    readOnlyLabelIds?: string[]
    noAccessLabelIds?: string[]
  }> = [],
): EffectiveLabelRestrictions => {
  const groupNoAccess = new Set<string>()
  const groupFullAccess = new Set<string>()
  const groupReadOnly = new Set<string>()

  groupLabels.forEach((gl) => {
    gl.noAccessLabelIds?.forEach((id) => {
      if (id) groupNoAccess.add(id)
    })
    gl.fullAccessLabelIds?.forEach((id) => {
      if (id) groupFullAccess.add(id)
    })
    gl.readOnlyLabelIds?.forEach((id) => {
      if (id) groupReadOnly.add(id)
    })
    gl.labelIds?.forEach((id) => {
      if (
        id &&
        !gl.noAccessLabelIds?.includes(id) &&
        !gl.readOnlyLabelIds?.includes(id)
      ) {
        groupFullAccess.add(id)
      }
    })
  })

  // 1. Group NoAccess takes highest precedence
  const finalNoAccess = new Set<string>(groupNoAccess)
  roleLabels?.noAccessLabelIds?.forEach((id) => {
    if (id && !groupFullAccess.has(id)) {
      finalNoAccess.add(id)
    }
  })

  // 2. Content restricted from role
  const finalContentRestricted: string[] = []
  roleLabels?.contentRestrictedLabelIds?.forEach((id) => {
    if (id && !finalNoAccess.has(id) && !groupFullAccess.has(id)) {
      finalContentRestricted.push(id)
    }
  })

  // 3. ReadOnly from groups and role
  const finalReadOnly = new Set<string>()
  groupReadOnly.forEach((id) => {
    if (!finalNoAccess.has(id) && !groupFullAccess.has(id)) {
      finalReadOnly.add(id)
    }
  })
  roleLabels?.readOnlyLabelIds?.forEach((id) => {
    if (id && !finalNoAccess.has(id) && !groupFullAccess.has(id)) {
      finalReadOnly.add(id)
    }
  })

  // 4. FullAccess from groups
  const finalFullAccess = new Set<string>()
  groupFullAccess.forEach((id) => {
    if (!finalNoAccess.has(id)) {
      finalFullAccess.add(id)
    }
  })

  return {
    readOnlyLabelIds: Array.from(finalReadOnly),
    contentRestrictedLabelIds: finalContentRestricted,
    noAccessLabelIds: Array.from(finalNoAccess),
    fullAccessLabelIds: Array.from(finalFullAccess),
  }
}

// Capabilities the backend (authz.go) grants once any label is in play, even
// when the role itself never grants them.
const LABEL_GRANTED_VIEW_CAPABILITIES = [
  CAPABILITY.CAN_VIEW_CONTROLS,
  CAPABILITY.CAN_VIEW_POLICIES,
]

const FULL_ACCESS_LABEL_CAPABILITIES = [
  CAPABILITY.CAN_UPDATE_CONTROLS,
  CAPABILITY.CAN_UPDATE_CONTROL_STATUS,
  CAPABILITY.CAN_UPDATE_CONTROL_OBJECTIVE,
  CAPABILITY.CAN_UPDATE_CONTROL_PROCEDURE,
  CAPABILITY.CAN_UPDATE_CONTROL_RESPONSIBILITY,
  CAPABILITY.CAN_UPDATE_CONTROL_REQUIRED_EVIDENCE,
  CAPABILITY.CAN_UPDATE_CONTROL_TEST_PROCEDURES,
  CAPABILITY.CAN_UPDATE_CONTROL_OWNER,
  CAPABILITY.CAN_LINK_CONTROL_FRAMEWORKS,
  CAPABILITY.CAN_UPDATE_CONTROL_NAME,
  CAPABILITY.CAN_UPDATE_CONTROL_ID,
  CAPABILITY.CAN_UPDATE_CONTROL_POLICY,
  CAPABILITY.CAN_LINK_CONTROL_EVIDENCE,
  CAPABILITY.CAN_LINK_CONTROL_REQUESTS,
  CAPABILITY.CAN_LINK_CONTROL_RISKS,
  CAPABILITY.CAN_TOGGLE_CONTROL_USES_NEW_AUDIT_SCAN,
  CAPABILITY.CAN_UPDATE_CONTROL_GENERAL_GUIDANCE,
  CAPABILITY.CAN_UPDATE_POLICIES,
  CAPABILITY.CAN_UPDATE_POLICY_RESPONSIBILITY,
  CAPABILITY.CAN_UPDATE_POLICY_OWNER,
  CAPABILITY.CAN_UPDATE_POLICY_DEPARTMENT,
  CAPABILITY.CAN_LINK_POLICY_CONTROLS,
  CAPABILITY.CAN_UPDATE_POLICY_DOCS,
  CAPABILITY.CAN_UPDATE_POLICY_NAME,
  CAPABILITY.CAN_UPDATE_POLICY_DESCRIPTION,
]

const promoteCapabilities = (
  capabilitiesMap: Partial<Record<CAPABILITY, PermissionInfo>>,
  capabilities: CAPABILITY[],
  explicitNever: Set<CAPABILITY>,
): void => {
  capabilities.forEach((cap) => {
    if (explicitNever.has(cap)) return
    const existing = capabilitiesMap[cap]
    if (existing && existing.rule !== PERMISSION_RULE.NEVER) return
    capabilitiesMap[cap] = {
      rule: PERMISSION_RULE.IF_GRANTED_PERMISSION,
      capabilityName: getEnumKeyName(CAPABILITY, cap),
      ruleName: getEnumKeyName(
        PERMISSION_RULE,
        PERMISSION_RULE.IF_GRANTED_PERMISSION,
      ),
    }
  })
}

export const computeEffectivePermissions = (
  accessRole: AccessRole,
  selectedGroups: UserGroupRecord[] = [],
  catalog: AccessRoleCatalog,
): Permissions => {
  const index = indexCatalog(catalog)
  const overrides = getPermissionOverrides(accessRole)

  const capabilitiesMap: Partial<Record<CAPABILITY, PermissionInfo>> = {}
  const explicitNever = new Set<CAPABILITY>()

  index.domains.forEach((domain) => {
    const domainRole = resolveDomainRole(index, accessRole, domain)
    const domainCaps = index.capabilitiesByDomain.get(domain.domain) || []
    const roleRules = index.rulesByDomainRole.get(
      `${domain.domain}:${domainRole}`,
    )

    domainCaps.forEach((cap) => {
      const override = overrides.get(cap.capability)
      const domainRule = roleRules?.get(cap.capability)

      if (
        override === PERMISSION_RULE.NEVER ||
        (override === undefined && domainRule === PERMISSION_RULE.NEVER)
      ) {
        explicitNever.add(cap.capability)
      }

      const rule = resolveCapabilityRule(
        index,
        overrides,
        domain.domain,
        domainRole,
        cap.capability,
      )
      capabilitiesMap[cap.capability] = {
        rule,
        capabilityName: cap.name || getEnumKeyName(CAPABILITY, cap.capability),
        ruleName: getEnumKeyName(PERMISSION_RULE, rule),
      }
    })
  })

  const roleLabels = {
    readOnlyLabelIds: accessRole.getReadOnlyLabelIdsList() || [],
    contentRestrictedLabelIds:
      accessRole.getContentRestrictedLabelIdsList() || [],
    noAccessLabelIds: accessRole.getNoAccessLabelIdsList() || [],
  }

  const groupLabels = selectedGroups.map((g) => ({
    labelIds: g.getLabelIdsList() || [],
    fullAccessLabelIds: g.getFullAccessLabelIdsList() || [],
    readOnlyLabelIds: g.getReadOnlyLabelIdsList() || [],
    noAccessLabelIds: g.getNoAccessLabelIdsList() || [],
  }))

  const effectiveLabels = combineLabelRestrictions(roleLabels, groupLabels)

  // Align with backend authz.go: promote view capabilities if labels are set
  if (
    effectiveLabels.fullAccessLabelIds.length > 0 ||
    effectiveLabels.readOnlyLabelIds.length > 0
  ) {
    promoteCapabilities(
      capabilitiesMap,
      LABEL_GRANTED_VIEW_CAPABILITIES,
      explicitNever,
    )
  }

  // Promote control/policy edit capabilities if full access labels exist
  if (effectiveLabels.fullAccessLabelIds.length > 0) {
    promoteCapabilities(
      capabilitiesMap,
      FULL_ACCESS_LABEL_CAPABILITIES,
      explicitNever,
    )
  }

  return {
    version: Date.now(),
    capabilities: capabilitiesMap,
    readOnlyLabelIds: effectiveLabels.readOnlyLabelIds,
    contentRestrictedLabelIds: effectiveLabels.contentRestrictedLabelIds,
    noAccessLabelIds: effectiveLabels.noAccessLabelIds,
    fullAccessLabelIds: effectiveLabels.fullAccessLabelIds,
  }
}

const parseCsvParam = (value: string | null): string[] | undefined => {
  if (!value) return undefined
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

export const parseImpersonationParams = (
  search: string,
): { roleId?: string; groupIds?: string[]; allowedRoutes?: string[] } => {
  const params = new URLSearchParams(search)
  return {
    roleId: params.get(IMPERSONATE_ROLE_PARAM) || undefined,
    groupIds: parseCsvParam(params.get(IMPERSONATE_GROUPS_PARAM)),
    allowedRoutes: parseCsvParam(params.get(IMPERSONATE_ALLOWED_ROUTES_PARAM)),
  }
}

// Drops every impersonation param, returning a search string ready to assign
// (leading '?' included, or empty when nothing is left).
export const stripImpersonationParams = (search: string): string => {
  const params = new URLSearchParams(search)
  IMPERSONATION_PARAMS.forEach((param) => params.delete(param))
  const searchStr = params.toString()
  return searchStr ? `?${searchStr}` : ''
}

export const buildImpersonateUrl = (
  baseUrl: string,
  roleId: string,
  groupIds: string[] = [],
  allowedRoutes?: string[],
): string => {
  const url = new URL(baseUrl, window.location.origin)
  url.searchParams.set(IMPERSONATE_ROLE_PARAM, roleId)
  if (groupIds.length > 0) {
    url.searchParams.set(IMPERSONATE_GROUPS_PARAM, groupIds.join(','))
  } else {
    url.searchParams.delete(IMPERSONATE_GROUPS_PARAM)
  }
  if (allowedRoutes && allowedRoutes.length > 0) {
    url.searchParams.set(
      IMPERSONATE_ALLOWED_ROUTES_PARAM,
      allowedRoutes.join(','),
    )
  } else {
    url.searchParams.delete(IMPERSONATE_ALLOWED_ROUTES_PARAM)
  }
  return url.pathname + url.search + url.hash
}

export const inferPreviewRoutes = (
  pathname: string,
): { allowedRoutes?: string[] } => {
  if (/\/controls(\/|$)/.test(pathname)) {
    return {
      allowedRoutes: ['controls/index', 'controls/show'],
    }
  }
  if (/\/policies(\/|$)/.test(pathname)) {
    return {
      allowedRoutes: ['policies/index', 'policies/show'],
    }
  }
  return {}
}

// The page a preview session falls back to: the first allowed route, scoped to
// the account in the URL when there is one.
export const previewHomeRoute = (
  pageContext: string | undefined,
  allowedRoutes: string[],
): string => {
  const firstRoute = allowedRoutes[0].split('?')[0]
  return pageContext ? `/${pageContext}/${firstRoute}` : `/${firstRoute}`
}

const normalizeRoute = (route: string): string =>
  route.replace(/^\//, '').split('?')[0].split('#')[0]

export const isPathAllowedInPreview = (
  pathname: string,
  pageContext?: string,
  allowedRoutes?: string[],
): boolean => {
  if (!allowedRoutes || allowedRoutes.length === 0) {
    return true
  }

  let relativePath = pathname
  if (pageContext) {
    const prefix = `/${pageContext}`
    if (relativePath.startsWith(prefix)) {
      relativePath = relativePath.slice(prefix.length)
    }
  } else {
    relativePath = relativePath.replace(/^\/[^/]+(\/|$)/, '')
  }

  relativePath = normalizeRoute(relativePath)

  if (!relativePath) {
    return false
  }

  return allowedRoutes.some((route) => {
    const cleanRoute = normalizeRoute(route)
    return (
      relativePath === cleanRoute ||
      relativePath.startsWith(`${cleanRoute}/`) ||
      relativePath === cleanRoute.split('/')[0]
    )
  })
}
